export type TroubleshootOption = 'pageLoop' | 'errorMessage';

export interface Step {
  title: string;
  description?: string;
  expandLabel?: string;
  expandSteps?: string[];
  expandNote?: string;
}

export interface AccordionItem {
  title: string;
  content: string;
  contentLinkUrl?: string;
  seeExampleLabel?: string;
  seeExampleContent?: string;
  note?: string;
  noteLinkUrl?: string;
  googleNote?: string;
  whatToDoLabel?: string;
  whatToDoItems?: Array<{ text: string; linkUrl?: string }>;
}

export interface TroubleshootContent {
  title: string;
  problemDescription?: string;
  problemDetail?: string;
  stepsTitle?: string;
  stepsSubtitle: string;
  stepsTip?: string;
  steps: Step[];
  secondarySection?: {
    title: string;
    items: AccordionItem[];
  };
}

const ACCOUNT_HELP_URL = 'https://support.microsoft.com/home/contact?linkquery=Help%20me%20sign%20in%20to%20my%20account';
const GOOGLE_SUPPORT_URL = 'https://support.google.com/accounts/troubleshooter/2402620?hl=en';
const META_SUPPORT_URL = 'https://www.meta.com/help/support/';
export const CONTACT_SUPPORT_URL = 'https://go.microsoft.com/fwlink/?LinkId=398371';

export const PAGE_LOOP_CONTENT: TroubleshootContent = {
  title: _TL_('The page won\'t load or it keeps refreshing'),
  problemDetail: _TL_('This is usually caused by a browser or network setting.'),
  stepsSubtitle: _TL_('One of these steps usually resolves it.'),
  steps: [
    {
      title: _TL_('Open a private or incognito window'),
      expandLabel: _TL_('How to do this?'),
      expandSteps: [
        _TL_('Open your browser menu.'),
        _TL_('Open a new private or incognito window.'),
        _TL_('Go to the sign-in page in that window.'),
        _TL_('Try to sign in.'),
      ],
    },
    {
      title: _TL_('Try a different browser or network'),
      expandLabel: _TL_('How to do this?'),
      expandSteps: [
        _TL_('Open a different web browser, if available.'),
        _TL_('Or connect to a different network, such as a mobile hotspot.'),
        _TL_('Go to the sign-in page.'),
        _TL_('Try to sign in.'),
      ],
    },
    {
      title: _TL_('Turn off browser extensions'),
      expandLabel: _TL_('How to do this?'),
      expandSteps: [
        _TL_('Open your browser settings.'),
        _TL_('Go to {{b}}Extensions{{/b}} or {{b}}Add\u2011ons{{/b}}.'),
        _TL_('Turn off extensions one at a time.'),
        _TL_('Try again after each change.'),
      ],
    },
    {
      title: _TL_('Clear your browser cache'),
      expandLabel: _TL_('How to do this?'),
      expandSteps: [
        _TL_('Open your browser settings.'),
        _TL_('Go to {{b}}Privacy{{/b}} or {{b}}History{{/b}}.'),
        _TL_('Clear cached files.'),
        _TL_('Close and reopen your browser. Then try to sign in.'),
      ],
    },
    {
      title: _TL_('Turn off your VPN'),
      expandLabel: _TL_('How to do this?'),
      expandSteps: [
        _TL_('Open your VPN app or network settings.'),
        _TL_('Turn the VPN {{b}}off{{/b}} or {{b}}disconnect{{/b}} it.'),
        _TL_('Confirm the VPN is no longer connected.'),
        _TL_('Try to sign in.'),
      ],
      expandNote: _TL_('Note: If your VPN is managed by work or school, you may need help from your IT team to turn it off.'),
    },
  ],
};

export const ERROR_MESSAGE_CONTENT: TroubleshootContent = {
  title: _TL_('I get an error explaining something is wrong or not working'),
  stepsSubtitle: _TL_('Use a private or incognito window, or clear your saved browser data (cache).'),
  stepsTip: _TL_('{{b}}Shortcut:{{/b}} Click into the search bar, and press {{b}}Ctrl{{/b}} (or \u2318 on Mac) + {{b}}Shift{{/b}} + {{b}}Delete{{/b}} to open cache settings.'),
  steps: [],
  secondarySection: {
    title: _TL_('If that didn\'t work, double-check the kind of account your email is connected to:'),
    items: [
      {
        title: _TL_('Your company\'s or school\'s account'),
        content: _TL_('You use the same email to access Microsoft apps that might be managed or set up by your organization. They can restrict certain features, see activity, and decide how you use work tools.'),
        seeExampleLabel: _TL_('See an example'),
        seeExampleContent: _TL_('You have a Microsoft work or school email (connected to an organization\'s account).'),
        googleNote: _TL_('{{i}}If your company or school primarily uses Google apps to get work done, they might use Google Workplace instead of Microsoft Entra ID. Use the steps outlined for Google and social media accounts.{{/i}}'),
        whatToDoLabel: _TL_('What to do'),
        whatToDoItems: [
          { text: _TL_('Your organization\'s IT team manages access, security rules, and verification for this account, including work email and company tools.') },
          { text: _TL_('Contact your IT team (often called IT Support or Help Desk), and share the exact error message or code you see.') },
        ],
      },
      {
        title: _TL_('Your own Microsoft account'),
        content: _TL_('This email might be the same as your work or school email, a different email @ [your company or school name], or one that\'s @gmail, @outlook, @hotmail, @live, or @msn, depending on which email you used to create your Microsoft account.'),
        seeExampleLabel: _TL_('See an example'),
        seeExampleContent: _TL_('You have an email you set up yourself (connected to a personal Microsoft account you might use for your own business).'),
        whatToDoLabel: _TL_('What to do'),
        whatToDoItems: [
          { text: _TL_('Use this {{a}}sign-in helper{{/a}} to fix password, security, or verification issues.'), linkUrl: ACCOUNT_HELP_URL },
        ],
      },
      {
        title: _TL_('A Google or social media account'),
        content: _TL_('You signed in, created an account, or accepted an invite to Microsoft Advertising using a Gmail address or a social media account. You might use this email for business, but you own it.'),
        seeExampleLabel: _TL_('See an example'),
        seeExampleContent: _TL_('You have a personal or business email (connected to a Google or Meta account).'),
        whatToDoLabel: _TL_('What to do'),
        whatToDoItems: [
          { text: _TL_('Make sure your email provider hasn\'t flagged any changes. In general, email and social accounts may require renewal, verification, unblocking, or other updates.') },
          { text: _TL_('{{i}}If you used \u201CSign in with Google\u201D (your Google account) or \u201CSign in with Meta,\u201D (your Meta account), trying to go through the Microsoft button and choosing or typing your email address won\'t work. But if you originally set up your Microsoft account by manually typing in your Gmail or email used with Meta, you can do it again. Try sticking to the same path.{{/i}}') },
          { text: _TL_('If you use a Google account, contact {{a}}Google support{{/a}}'), linkUrl: GOOGLE_SUPPORT_URL },
          { text: _TL_('If you use a Meta account, contact {{a}}Meta support{{/a}}'), linkUrl: META_SUPPORT_URL },
        ],
      },
    ],
  },
};

export const CONTENT_MAP: Record<string, TroubleshootContent> = {
  pageLoop: PAGE_LOOP_CONTENT,
  errorMessage: ERROR_MESSAGE_CONTENT,
};
